"use client";

import { useEffect, useRef, useState } from "react";

import { ProductShowcase } from "@/components/home/product-showcase";
import type { Product } from "@/types";

const EMPTY_PRODUCTS: Product[] = [];

export function RecentlyViewedHome({ products = EMPTY_PRODUCTS }: { products?: Product[] }) {
  const [viewed, setViewed] = useState<Product[]>([]);
  const fetchedSlugsRef = useRef<string>("");

  useEffect(() => {
    let slugs: string[] = [];
    try {
      const stored = JSON.parse(localStorage.getItem("recentlyViewed") ?? "[]") as Array<{ slug?: string } | string>;
      slugs = stored.map((entry) => (typeof entry === "string" ? entry : entry.slug ?? "")).filter(Boolean);
    } catch {
      slugs = [];
    }

    if (slugs.length === 0) return;

    if (products.length > 0) {
      const matched = slugs
        .map((slug) => products.find((p) => p.slug === slug))
        .filter((p): p is Product => Boolean(p))
        .slice(0, 5);
      if (matched.length > 0) {
        setViewed(matched);
        return;
      }
    }

    // Efficiently fetch only the viewed slugs once
    const targetSlugs = slugs.slice(0, 5).join(",");
    if (!targetSlugs || fetchedSlugsRef.current === targetSlugs) return;
    fetchedSlugsRef.current = targetSlugs;

    let active = true;
    fetch(`/api/products?slugs=${encodeURIComponent(targetSlugs)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data?.items) setViewed(data.items);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [products]);

  if (viewed.length === 0) return null;

  return (
    <ProductShowcase
      title="Recently viewed"
      subtitle="Products you looked at recently."
      products={viewed}
      href="/search"
    />
  );
}
